import type { BibleBook } from "./types";

/** Genesis to Deuteronomy — the Torah, the first five. */
export const LAW: BibleBook[] = [
  {
    slug: "genesis",
    name: "Genesis",
    testament: "old",
    genre: "law",
    order: 1,
    chapters: 50,
    author:
      "Traditionally Moses; most scholars now see a compilation of older sources",
    written:
      "Reaching its final form around the sixth century BC, from much older material",
    audience: "Israel, being told where it came from",
    argument:
      "A book about beginnings that spends four fifths of its length on one dysfunctional family, because the point is not how the world started but who is keeping a promise inside it.",
    summary: [
      "Genesis divides almost exactly in two and the halves are nothing alike. Chapters 1 to 11 move at enormous scale — creation, a flood, a tower, whole civilisations dismissed in a verse. Then chapter 12 arrives and the camera drops to one childless couple in their seventies, and it stays with their family for the remaining thirty-nine chapters.",
      "That change of scale is the argument. The book is not primarily interested in cosmology; it is interested in whether a promise made to one family can survive that family, and the family it chose is a catalogue of deception, favouritism and violence. Abraham lies about his wife twice. Jacob cheats his brother and is cheated in turn. His sons sell one of their own into slavery and tell their father he was eaten.",
      "It ends with that son running Egypt and the family fed, and with a sentence that has been read as the book's whole thesis: you meant evil against me, but God meant it for good. Nothing has been tidied up. The promise has simply not failed.",
    ],
    startHere:
      "Chapter 12, where the promise is made, unless you specifically came for the creation accounts. Reading 1 to 11 first is what stalls most attempts at Genesis — the genealogies in chapters 5 and 10 are where people stop.",
    themes: ["promise", "family", "blessing and rivalry", "exile and return"],
    keyChapters: [
      {
        chapter: 12,
        why: "The promise to Abraham, which the remaining thirty-nine chapters are about keeping.",
      },
      {
        chapter: 22,
        why: "The binding of Isaac, the passage that has troubled readers for three thousand years and has never been made comfortable.",
      },
      {
        chapter: 50,
        why: "You meant evil against me, but God meant it for good — the sentence the whole book has been building toward.",
      },
    ],
    known: {
      text: "In the beginning, God created the heavens and the earth.",
      ref: "Genesis 1:1",
    },
    misread:
      "That it is mainly about creation. Two chapters are; forty-eight are about a family. Reading it as a book of origins produces endless argument about the first page and no acquaintance with the rest, which is where the book actually lives.",
    faq: [
      {
        q: "Who wrote the book of Genesis?",
        a: "Tradition attributes it to Moses. Most modern scholarship sees it as a compilation reaching its final form far later, drawing on older written and oral sources — a view held by a great many Christians as well as by critical scholars.",
      },
      {
        q: "How many chapters are in Genesis?",
        a: "Fifty. The first eleven cover the whole world; the remaining thirty-nine cover four generations of one family.",
      },
    ],
    topics: ["bible-verses-about-rest"],
  },
  {
    slug: "exodus",
    name: "Exodus",
    testament: "old",
    genre: "law",
    order: 2,
    chapters: 40,
    author: "Traditionally Moses",
    written: "Final form likely sixth century BC, from older traditions",
    audience: "A people defining itself by having been slaves",
    argument:
      "A rescue story that spends its second half on building regulations, because the book thinks being freed and learning to live free are the same subject.",
    summary: [
      "Everybody knows the first half: a baby in a basket, a burning bush, ten plagues, a sea that opens. It is the most cinematic sequence in the Bible and it is over by chapter 15.",
      "What follows surprises every first-time reader. The people are free for about three days before they start complaining, and the rest of the book is law, covenant and a tabernacle described twice in exhaustive detail — the specification, and then the construction, both in full. Modern readers find those chapters unreadable. Ancient readers understood them as the point: God moving in.",
      "In the middle of it sits chapter 32, where the people build a golden calf while Moses is still up the mountain collecting the instructions. The book is honest about how quickly the rescue was forgotten, and it does not resolve that tension so much as build a structure around it.",
    ],
    startHere:
      "Chapter 3, at the bush. Chapters 1 and 2 are setup you can absorb from context, and chapter 3 is where the book states its terms.",
    themes: ["rescue", "covenant", "presence", "law"],
    keyChapters: [
      {
        chapter: 3,
        why: "The burning bush, and the name given in answer to a question that had not quite been asked.",
      },
      {
        chapter: 14,
        why: "The sea, which is the scene everybody pictures when they hear the word Exodus.",
      },
      {
        chapter: 32,
        why: "The golden calf, built while Moses was still collecting the instructions against it.",
      },
    ],
    known: {
      text: "Yahweh will fight for you, and you shall be still.",
      ref: "Exodus 14:14",
    },
    misread:
      "That it ends at the Red Sea. The escape is a quarter of the book; the remaining three quarters are about what a freed people is supposed to become, which is the harder and less filmable half.",
    faq: [
      {
        q: "What is the book of Exodus about?",
        a: "Two things joined: the rescue of Israel from Egypt, and the law and tabernacle that follow it. The book treats being freed and learning to live free as one subject.",
      },
      {
        q: "How many chapters are in Exodus?",
        a: "Forty. The escape is complete by chapter 15; the remaining twenty-five are covenant, law and the tabernacle.",
      },
    ],
    topics: ["bible-verses-about-rest"],
  },
  {
    slug: "leviticus",
    name: "Leviticus",
    testament: "old",
    genre: "law",
    order: 3,
    chapters: 27,
    author: "Traditionally Moses",
    written: "Priestly material, final form likely sixth to fifth century BC",
    audience: "Priests, and a people camped around a tent",
    argument:
      "The book where most reading plans die, and it is a manual for how an ordinary person handles proximity to holiness without being destroyed by it.",
    summary: [
      "Leviticus is the reason January reading plans collapse in the third week. It is twenty-seven chapters of sacrifice, skin disease, mould, food, blood and festival calendars, with almost no narrative to carry a reader through.",
      "Read as a manual rather than as a story it makes considerably more sense. The question it is answering is practical: a holy God has just moved into the middle of the camp, which is dangerous, and here is how ordinary people conduct themselves at that range. Almost every regulation is about maintaining a distinction — clean and unclean, holy and common — in a world where those categories were assumed to be physical rather than moral.",
      "Chapter 19 is the one to know. Buried in a chapter of miscellaneous law, next to instructions about harvest edges and honest weights, is the line Jesus later names as the second greatest commandment.",
    ],
    startHere:
      "Chapter 19, which is the most readable chapter in the book and contains its most famous sentence. Chapter 16, the Day of Atonement, is the one to read next if you want to understand Hebrews later.",
    themes: ["holiness", "sacrifice", "clean and unclean", "atonement"],
    keyChapters: [
      {
        chapter: 16,
        why: "The Day of Atonement, which Hebrews spends several chapters explaining and which is unreadable without it.",
      },
      {
        chapter: 19,
        why: "Love your neighbour as yourself, sitting among instructions about harvest edges and honest weights.",
      },
      {
        chapter: 25,
        why: "The jubilee — debts cancelled and land returned every fiftieth year, the most radical economics in the Bible.",
      },
    ],
    known: {
      text: "you shall love your neighbor as yourself. I am Yahweh.",
      ref: "Leviticus 19:18",
    },
    misread:
      "That it is a list of arbitrary rules. Nearly everything in it is doing one of two jobs — marking a distinction, or providing a way back after a distinction has been broken — and the second job is what the book is actually for.",
    faq: [
      {
        q: "Why is Leviticus so hard to read?",
        a: "Because it is a manual rather than a narrative, written for priests in a culture whose categories of clean and unclean were physical as much as moral. It is not designed to be read straight through, and reading plans that require that mostly fail here.",
      },
      {
        q: "What is the main message of Leviticus?",
        a: "How ordinary people live at close range to holiness — and, more practically, what happens when that goes wrong, which is what the sacrificial system exists to answer.",
      },
    ],
  },
  {
    slug: "numbers",
    name: "Numbers",
    testament: "old",
    genre: "law",
    order: 4,
    chapters: 36,
    author: "Traditionally Moses",
    written: "Final form likely sixth to fifth century BC",
    audience: "The generation after the one that refused to go in",
    argument:
      "A book named after two censuses, which is really about the forty years that happened because a people got to the edge of the promise and decided not to enter.",
    summary: [
      "The English name comes from the two head-counts that bracket the book, which is a misleading title for what is inside. Numbers is the wilderness book: the stretch between leaving Egypt and arriving, and the reason that stretch took forty years rather than eleven days.",
      "Chapter 13 is the hinge. Twelve scouts go into the land; ten come back saying it cannot be done. The people believe the ten, and the consequence is a generation that dies in the desert. Almost everything after that chapter is life lived under a verdict, and the book is unusually frank about what that does to people — rebellion, complaint, a plague, water struck out of a rock in anger.",
      "It also contains the blessing every congregation has heard, spoken over precisely these people. That juxtaposition is deliberate.",
    ],
    startHere:
      "Chapter 13. The censuses and camp arrangements in 1 to 10 are documentation; chapter 13 is where the book's actual subject begins.",
    themes: ["wilderness", "complaint", "census", "a generation lost"],
    keyChapters: [
      {
        chapter: 6,
        why: "The blessing every congregation has heard, spoken over people about to fail badly.",
      },
      {
        chapter: 13,
        why: "The scouts. Ten say it cannot be done, the people believe them, and forty years follow.",
      },
      {
        chapter: 20,
        why: "Moses strikes the rock in anger and is told he will not enter the land.",
      },
    ],
    known: {
      text: "Yahweh bless you, and keep you.",
      ref: "Numbers 6:24",
    },
    misread:
      "That the forty years were a punishment for a moral failure. The text presents it as a consequence of a specific refusal — the people were asked whether they believed the promise could be kept and answered no, in front of witnesses.",
    faq: [
      {
        q: "Why is the book called Numbers?",
        a: 'From the two censuses that open and close it. The Hebrew name, taken from its first line, means "in the wilderness", which describes the contents considerably better.',
      },
      {
        q: "What happens in the book of Numbers?",
        a: "Israel reaches the edge of the promised land, sends in scouts, refuses to enter, and spends forty years in the desert until that generation has died. The second census counts the ones who go in.",
      },
    ],
  },
  {
    slug: "deuteronomy",
    name: "Deuteronomy",
    testament: "old",
    genre: "law",
    order: 5,
    chapters: 34,
    author:
      "Traditionally Moses; scholars associate its core with the seventh century BC",
    written:
      "Core likely seventh century BC, presented as Moses' final speeches",
    audience: "A generation about to enter a land none of them have seen",
    argument:
      "Three farewell speeches from a man who is not allowed to cross the river, restating the law for people who were children when it was given.",
    summary: [
      "Deuteronomy means second law, and that is roughly what it is: the covenant restated for a generation that did not stand at Sinai. Structurally it is Moses' last words, delivered on the far bank of the Jordan, to a people about to go somewhere he cannot follow.",
      "That situation gives the book its tone, which is urgent, repetitive and often moving in a way the earlier legal material is not. The instruction that faith be taught while sitting at home, walking on the road, lying down and getting up is here — it locates the whole enterprise in ordinary conversation rather than in ceremony.",
      "It is also the book Jesus quotes from most often, including all three answers in the wilderness temptation, which makes it far more present in the New Testament than its position in a reading plan suggests.",
    ],
    startHere:
      "Chapter 6, which contains the Shema and the household instruction. It is the densest short passage in the Old Testament and everything else in the book orbits it.",
    themes: ["covenant renewal", "memory", "teaching children", "choosing"],
    keyChapters: [
      {
        chapter: 6,
        why: "The Shema, and the instruction to talk about this while sitting, walking, lying down and getting up.",
      },
      {
        chapter: 8,
        why: "The warning about what prosperity does to memory, which is the book's most modern paragraph.",
      },
      {
        chapter: 30,
        why: "Life and death set before them, with the instruction to choose life.",
      },
    ],
    known: {
      text: "You shall love Yahweh your God with all your heart, with all your soul, and with all your might.",
      ref: "Deuteronomy 6:5",
    },
    misread:
      "That it is a repeat of Exodus and Leviticus and can therefore be skipped. It is a restatement with a different purpose — preaching rather than legislation — and it is the Old Testament book the New Testament leans on hardest.",
    faq: [
      {
        q: "What does Deuteronomy mean?",
        a: "Second law, from the Greek. It is the covenant restated for the generation that grew up in the wilderness rather than a new body of legislation.",
      },
      {
        q: "Why does Jesus quote Deuteronomy so often?",
        a: "All three of his answers during the wilderness temptation come from it, and the greatest commandment is Deuteronomy 6:5. It was evidently central to how he read the Old Testament.",
      },
    ],
  },
];
